import {prop} from 'ramda';

import {DASHBOARD_TILE_SIZES} from '../consts/dashboardTileSizes';
import {DashboardTileId} from '../types/DashboardTileId';
import {DashboardTileSize} from '../types/DashboardTileSize';
import {
  allocateDashboardGrid,
  compareDashboardGridPositions,
  dashboardGridTilesOverlap,
  PositionedDashboardGridTile,
} from './allocateDashboardGrid';
import {closeEmptyDashboardRows} from './closeEmptyDashboardRows';

export function resizeDashboardGrid(
  tiles: PositionedDashboardGridTile[],
  activeId: DashboardTileId,
  size: DashboardTileSize
): PositionedDashboardGridTile[] {
  const active = tiles.find((tile) => tile.id === activeId);

  if (active === undefined || active.size === size) {
    return tiles;
  }

  const width = prop(size, DASHBOARD_TILE_SIZES).columns;
  // Snap to the aligned slot that holds the current column; it always ends inside the board.
  const resizedActive = {...active, size, column: Math.floor(active.column / width) * width};
  const readingOrder = [...tiles].sort(compareDashboardGridPositions);
  const kept = readingOrder.filter(
    (tile) =>
      tile.id !== activeId &&
      tile.row <= active.row &&
      !dashboardGridTilesOverlap(tile, resizedActive)
  );
  const keptIds = new Set(kept.map((tile) => tile.id));
  const queue = readingOrder
    .filter((tile) => tile.id !== activeId && !keptIds.has(tile.id))
    .map((tile) => ({id: tile.id, size: tile.size}));
  const reflowed = allocateDashboardGrid(queue, {
    occupied: [...kept, resizedActive],
    startRow: active.row,
  });

  // Reflowing can lift every tile out of a row, which would leave it as a blank band.
  return reflowed === null
    ? tiles
    : closeEmptyDashboardRows([...kept, resizedActive, ...reflowed]).sort(
        compareDashboardGridPositions
      );
}
